import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  SafeAreaView,
  StatusBar,
  Modal,
  TextInput,
  Alert,
  Image,
} from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import AsyncStorage from '@react-native-async-storage/async-storage';
import axios from 'axios';

// ==================== API CONFIGURATION ====================
const API_BASE_URL = 'https://api.jobeng.ai';
const API_ENDPOINTS = {
  companyProfile: `${API_BASE_URL}/api/data/company_profiles`,
  contractTemplates: `${API_BASE_URL}/api/data/contract_templates`,
};

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'X-API-Key': '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef',
  },
});

api.interceptors.request.use(
  async (config) => {
    try {
      const token = await AsyncStorage.getItem('auth_token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      const companyId = await AsyncStorage.getItem('company_id');
      if (companyId) {
        config.params = { ...config.params, company_id: parseInt(companyId) };
      }
    } catch (error) {
      console.error('Error in request interceptor:', error);
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Solidity Smart Contract Templates
export const SOLIDITY_TEMPLATES = {
  BASIC_EMPLOYMENT: `...`, // keep your existing Solidity code
  ADVANCED_EMPLOYMENT: `...`,
  FREELANCER_CONTRACT: `...`,
  INTERNSHIP_CONTRACT: `...`,
};

// FIDIC Templates (static)
export const FIDIC_TEMPLATES = [
  {
    id: 'fidic_red',
    name: 'FIDIC Red Book',
    description: 'Conditions of Contract for Construction (for building and engineering works designed by the Employer)',
    icon: 'book-open-variant',
    color: '#E53E3E',
    features: ['Employer-designed', 'Traditional', 'Red Book', 'Construction'],
  },
  {
    id: 'fidic_yellow',
    name: 'FIDIC Yellow Book',
    description: 'Conditions of Contract for Plant & Design-Build (for electrical and mechanical plant, and for building and engineering works designed by the Contractor)',
    icon: 'flash',
    color: '#D69E2E',
    features: ['Contractor-designed', 'Plant & Design-Build', 'Yellow Book'],
  },
  {
    id: 'fidic_silver',
    name: 'FIDIC Silver Book',
    description: 'Conditions of Contract for EPC/Turnkey Projects (for projects on a turnkey basis)',
    icon: 'silverware-fork-knife',
    color: '#718096',
    features: ['EPC', 'Turnkey', 'Silver Book', 'Lump Sum'],
  },
  {
    id: 'fidic_green',
    name: 'FIDIC Green Book',
    description: 'Short Form of Contract (for simple or repetitive work, or for contracts of relatively low value)',
    icon: 'leaf',
    color: '#38A169',
    features: ['Short Form', 'Simple Works', 'Low Value', 'Green Book'],
  },
];

const SmartContractTemplates = ({ navigation, route }) => {
  const [selectedTemplate, setSelectedTemplate] = useState('BASIC_EMPLOYMENT');
  const [customTemplate, setCustomTemplate] = useState('');
  const [showCustomModal, setShowCustomModal] = useState(false);
  const [savedTemplates, setSavedTemplates] = useState([]);
  const [logoUrl, setLogoUrl] = useState(null);

  useEffect(() => {
    loadSavedTemplates();
    loadCompanyLogo();
  }, []);

  const loadCompanyLogo = async () => {
    try {
      const cachedProfile = await AsyncStorage.getItem('company_profile');
      if (cachedProfile) {
        const profile = JSON.parse(cachedProfile);
        setLogoUrl(profile.logo || null);
      }
      const companyId = await AsyncStorage.getItem('company_id');
      if (companyId) {
        const response = await api.get(API_ENDPOINTS.companyProfile, {
          params: { company_id: parseInt(companyId) },
        });
        if (response.data?.success && response.data.data && response.data.data.length > 0) {
          const latestProfile = response.data.data[0];
          setLogoUrl(latestProfile.logo || null);
          await AsyncStorage.setItem('company_profile', JSON.stringify(latestProfile));
        }
      }
    } catch (error) {
      console.warn('Error loading company logo:', error);
    }
  };

  const loadSavedTemplates = async () => {
    try {
      const saved = await AsyncStorage.getItem('smart_contract_templates');
      if (saved) {
        setSavedTemplates(JSON.parse(saved));
      }
    } catch (error) {
      console.error('Error loading templates:', error);
    }
  };

  const saveCustomTemplate = async () => {
    if (!customTemplate.trim()) {
      Alert.alert('Error', 'Please enter a custom template');
      return;
    }

    const newTemplate = {
      id: `CUSTOM_${Date.now()}`,
      name: 'Custom Template',
      code: customTemplate,
      createdAt: new Date().toISOString()
    };

    const updatedTemplates = [...savedTemplates, newTemplate];
    setSavedTemplates(updatedTemplates);

    try {
      await AsyncStorage.setItem('smart_contract_templates', JSON.stringify(updatedTemplates));
      setShowCustomModal(false);
      setCustomTemplate('');
      Alert.alert('Success', 'Custom template saved successfully');
    } catch (error) {
      console.error('Error saving template:', error);
      Alert.alert('Error', 'Failed to save template');
    }
  };

  const selectTemplate = (templateId, templateType = 'solidity') => {
    setSelectedTemplate(templateId);
    if (route.params?.onTemplateSelect) {
      if (templateType === 'fidic') {
        const fidicTemplate = FIDIC_TEMPLATES.find(t => t.id === templateId);
        route.params.onTemplateSelect(templateId, fidicTemplate);
      } else {
        route.params.onTemplateSelect(templateId, SOLIDITY_TEMPLATES[templateId]);
      }
      navigation.goBack();
    } else {
      // If not used as a picker, just stay on the screen
    }
  };

  const contractTypes = [
    {
      id: 'BASIC_EMPLOYMENT',
      name: 'Basic Employment Contract',
      description: 'Standard employment contract with basic terms and conditions',
      icon: 'account-tie',
      color: '#3B82F6',
      features: ['Salary Management', 'Basic Benefits', 'Contract Terms', 'Termination Clauses']
    },
    {
      id: 'ADVANCED_EMPLOYMENT',
      name: 'Advanced Employment Contract',
      description: 'Comprehensive employment contract with advanced features',
      icon: 'office-building',
      color: '#10B981',
      features: ['Payroll Processing', 'Performance Reviews', 'Vacation Management', 'Role-based Access']
    },
    {
      id: 'FREELANCER_CONTRACT',
      name: 'Freelancer Contract',
      description: 'Project-based contract with milestone payments',
      icon: 'laptop',
      color: '#F59E0B',
      features: ['Milestone Tracking', 'Escrow Payments', 'Dispute Resolution', 'Project Management']
    },
    {
      id: 'INTERNSHIP_CONTRACT',
      name: 'Internship Contract',
      description: 'Educational internship with learning objectives',
      icon: 'school',
      color: '#8B5CF6',
      features: ['Learning Objectives', 'Weekly Evaluations', 'Stipend Management', 'Progress Tracking']
    },
  ];

  const renderTemplateCard = (template) => (
    <TouchableOpacity
      key={template.id}
      style={[
        styles.templateCard,
        selectedTemplate === template.id && styles.selectedTemplateCard
      ]}
      onPress={() => selectTemplate(template.id, 'solidity')}
    >
      <LinearGradient
        colors={[template.color, `${template.color}20`]}
        style={styles.templateGradient}
      >
        <MaterialCommunityIcons name={template.icon} size={32} color="#FFFFFF" />
      </LinearGradient>
      
      <View style={styles.templateContent}>
        <Text style={styles.templateName}>{template.name}</Text>
        <Text style={styles.templateDescription}>{template.description}</Text>
        
        <View style={styles.featuresList}>
          {template.features.map((feature, index) => (
            <View key={index} style={styles.featureItem}>
              <MaterialCommunityIcons name="check-circle" size={14} color={template.color} />
              <Text style={styles.featureText}>{feature}</Text>
            </View>
          ))}
        </View>
      </View>
      
      {selectedTemplate === template.id && (
        <MaterialCommunityIcons name="check-circle" size={24} color={template.color} />
      )}
    </TouchableOpacity>
  );

  const renderFidicCard = (template) => (
    <TouchableOpacity
      key={template.id}
      style={[
        styles.templateCard,
        selectedTemplate === template.id && styles.selectedTemplateCard
      ]}
      onPress={() => selectTemplate(template.id, 'fidic')}
    >
      <LinearGradient
        colors={[template.color, `${template.color}20`]}
        style={styles.templateGradient}
      >
        <MaterialCommunityIcons name={template.icon} size={32} color="#FFFFFF" />
      </LinearGradient>
      
      <View style={styles.templateContent}>
        <Text style={styles.templateName}>{template.name}</Text>
        <Text style={styles.templateDescription}>{template.description}</Text>
        
        <View style={styles.featuresList}>
          {template.features.map((feature, index) => (
            <View key={index} style={styles.featureItem}>
              <MaterialCommunityIcons name="check-circle" size={14} color={template.color} />
              <Text style={styles.featureText}>{feature}</Text>
            </View>
          ))}
        </View>
      </View>
      
      {selectedTemplate === template.id && (
        <MaterialCommunityIcons name="check-circle" size={24} color={template.color} />
      )}
    </TouchableOpacity>
  );

  return (
    <SafeAreaView style={styles.container}>
      <StatusBar barStyle="light-content" backgroundColor="#1F2937" />
      
      <LinearGradient colors={['#1F2937', '#374151']} style={styles.header}>
        <TouchableOpacity style={styles.backButton} onPress={() => navigation.goBack()}>
          <MaterialCommunityIcons name="arrow-left" size={24} color="#FFFFFF" />
        </TouchableOpacity>

        {logoUrl ? (
          <Image source={{ uri: (logoUrl && logoUrl.startsWith('http')) ? logoUrl : undefined }} style={styles.headerLogo} />
        ) : (
          <MaterialCommunityIcons name="file-document-outline" size={30} color="#FFFFFF" />
        )}
        
        <View style={styles.headerInfo}>
          <Text style={styles.headerTitle}>Smart Contract Templates</Text>
          <Text style={styles.headerSubtitle}>Choose a contract template</Text>
        </View>
        
        <TouchableOpacity style={styles.customButton} onPress={() => setShowCustomModal(true)}>
          <MaterialCommunityIcons name="plus" size={24} color="#FFFFFF" />
        </TouchableOpacity>
      </LinearGradient>

      <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
        {/* Solidity Templates */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Solidity Smart Contracts</Text>
          <Text style={styles.sectionDescription}>
            Blockchain-based smart contract templates (Ethereum)
          </Text>
          <View style={styles.templatesGrid}>
            {contractTypes.map(renderTemplateCard)}
          </View>
        </View>

        {/* FIDIC Templates */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>FIDIC Contract Templates</Text>
          <Text style={styles.sectionDescription}>
            International standard construction contracts (Red, Yellow, Silver, Green Books)
          </Text>
          <View style={styles.templatesGrid}>
            {FIDIC_TEMPLATES.map(renderFidicCard)}
          </View>
        </View>

        {/* Saved Custom Templates */}
        {savedTemplates.length > 0 && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Custom Templates</Text>
            <Text style={styles.sectionDescription}>
              Your saved custom contract templates
            </Text>
            {savedTemplates.map((template) => (
              <TouchableOpacity
                key={template.id}
                style={styles.customTemplateCard}
                onPress={() => selectTemplate(template.id, 'solidity')}
              >
                <MaterialCommunityIcons name="file-code" size={24} color="#6B7280" />
                <View style={styles.customTemplateInfo}>
                  <Text style={styles.customTemplateName}>{template.name}</Text>
                  <Text style={styles.customTemplateDate}>
                    Created: {new Date(template.createdAt).toLocaleDateString()}
                  </Text>
                </View>
              </TouchableOpacity>
            ))}
          </View>
        )}

        {/* Template Preview for Solidity */}
        {selectedTemplate && SOLIDITY_TEMPLATES[selectedTemplate] && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Template Preview</Text>
            <View style={styles.codePreview}>
              <Text style={styles.codeText}>
                {SOLIDITY_TEMPLATES[selectedTemplate].substring(0, 500)}...
              </Text>
            </View>
          </View>
        )}
      </ScrollView>

      {/* Custom Template Modal */}
      <Modal visible={showCustomModal} animationType="slide" presentationStyle="pageSheet">
        <SafeAreaView style={styles.modalContainer}>
          <View style={styles.modalHeader}>
            <TouchableOpacity onPress={() => setShowCustomModal(false)}>
              <Text style={styles.modalCancel}>Cancel</Text>
            </TouchableOpacity>
            <Text style={styles.modalTitle}>Custom Template</Text>
            <TouchableOpacity onPress={saveCustomTemplate}>
              <Text style={styles.modalSave}>Save</Text>
            </TouchableOpacity>
          </View>
          
          <TextInput
            style={styles.customTemplateInput}
            value={customTemplate}
            onChangeText={setCustomTemplate}
            placeholder="Enter your custom Solidity contract code here..."
            multiline
            textAlignVertical="top"
          />
        </SafeAreaView>
      </Modal>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#667eea',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 16,
    paddingTop: 20,
  },
  backButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  headerLogo: {
    width: 30,
    height: 30,
    borderRadius: 15,
    marginLeft: 12,
    resizeMode: 'cover',
  },
  headerInfo: {
    flex: 1,
    marginLeft: 12,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#FFFFFF',
  },
  headerSubtitle: {
    fontSize: 14,
    color: '#D1D5DB',
    marginTop: 2,
  },
  customButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  content: {
    flex: 1,
    padding: 20,
  },
  section: {
    marginBottom: 32,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1F2937',
    marginBottom: 8,
  },
  sectionDescription: {
    fontSize: 14,
    color: '#6B7280',
    marginBottom: 20,
    lineHeight: 20,
  },
  templatesGrid: {
    gap: 16,
  },
  templateCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 20,
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 2,
    borderColor: 'transparent',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  selectedTemplateCard: {
    borderColor: '#3B82F6',
    backgroundColor: '#F0F9FF',
  },
  templateGradient: {
    width: 60,
    height: 60,
    borderRadius: 30,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 16,
  },
  templateContent: {
    flex: 1,
  },
  templateName: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#1F2937',
    marginBottom: 4,
  },
  templateDescription: {
    fontSize: 14,
    color: '#6B7280',
    marginBottom: 12,
    lineHeight: 20,
  },
  featuresList: {
    gap: 6,
  },
  featureItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  featureText: {
    fontSize: 12,
    color: '#374151',
  },
  customTemplateCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    padding: 16,
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
    borderWidth: 1,
    borderColor: '#E5E7EB',
  },
  customTemplateInfo: {
    marginLeft: 12,
    flex: 1,
  },
  customTemplateName: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1F2937',
  },
  customTemplateDate: {
    fontSize: 12,
    color: '#6B7280',
    marginTop: 2,
  },
  codePreview: {
    backgroundColor: '#1F2937',
    borderRadius: 8,
    padding: 16,
  },
  codeText: {
    fontFamily: 'monospace',
    fontSize: 12,
    color: '#D1D5DB',
    lineHeight: 18,
  },
  modalContainer: {
    flex: 1,
    backgroundColor: '#FFFFFF',
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  modalCancel: {
    fontSize: 16,
    color: '#6B7280',
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1F2937',
  },
  modalSave: {
    fontSize: 16,
    color: '#3B82F6',
    fontWeight: '600',
  },
  customTemplateInput: {
    flex: 1,
    padding: 20,
    fontSize: 14,
    fontFamily: 'monospace',
    color: '#1F2937',
  },
});

export default SmartContractTemplates;